import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle2, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import client from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { formatDateSafe } from '../../utils/formatDateSafe';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';

import { etiquetaProyecto } from '../../utils/formatProject';
import DetalleNoDisponible, { motivoDeCarga } from '../../components/shared/DetalleNoDisponible';
// ⚠ ANTI-DIVERGENCIA: espeja `PTO_LEVELS` del backend (ptoApprovalChainService).
const LEVEL_ROLE = { 1: 'Ingeniero(a)', 2: 'Socio(a)' };
const CHAIN_STATE = {
  approved:  { label: 'Aprobado',  color: '#16a34a', bg: '#dcfce7' },
  pending:   { label: 'Pendiente', color: '#a16207', bg: '#fef9c3' },
  waiting:   { label: 'En espera', color: '#64748b', bg: '#f1f5f9' },
  skipped:   { label: 'Omitido',   color: '#6d28d9', bg: '#ede9fe' },
  rejected:  { label: 'Denegado',  color: '#dc2626', bg: '#fee2e2' },
  cancelled: { label: 'Cancelado', color: '#64748b', bg: '#f1f5f9' },
};

const STATUS_CONFIG = {
  pending:   { label: 'Pendiente',  color: '#eab308', bg: '#fef9c3', icon: Clock },
  approved:  { label: 'Aprobada',   color: '#16a34a', bg: '#dcfce7', icon: CheckCircle2 },
  denied:    { label: 'Denegada',   color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  cancelled: { label: 'Cancelada',  color: '#64748b', bg: '#f1f5f9', icon: AlertCircle },
};

// ⚰ 'personal' retirado en V59 (fuera del CHECK de pto_requests).
const TYPE_LABELS = { vacation: 'Vacaciones', sick: 'Enfermedad' };

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);  // B-1: el motivo, en vez del rebote mudo
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [overlapping, setOverlapping] = useState([]);

  useEffect(() => {
    client.get(`/api/vacation/requests/${id}`)
      .then(r => {
        setRequest(r.data);
        // Fetch approved vacations that overlap with this request's date range
        if (r.data.start_date && r.data.end_date) {
          client.get('/api/vacation/calendar', {
            params: {
              month: new Date(r.data.start_date).getMonth() + 1,
              year: new Date(r.data.start_date).getFullYear(),
            }
          }).then(calRes => {
            const start = r.data.start_date.slice(0, 10);
            const end = r.data.end_date.slice(0, 10);
            const overlap = calRes.data.filter(v =>
              v.id !== parseInt(id) &&
              v.start_date.slice(0, 10) <= end &&
              v.end_date.slice(0, 10) >= start
            );
            setOverlapping(overlap);
          }).catch(() => {});
        }
      })
      .catch(err => { console.error(err); setErrorCarga(motivoDeCarga(err)); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // ⚠ V62 — `formatDateSafe` (V46): son columnas DATE, y el parseo ingenuo las corría un día.
  const formatDate = (d) =>
    formatDateSafe(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  async function handleAction(action) {
    setActionLoading(true);
    try {
      // 🔴 NO uses la respuesta del POST como estado de la pantalla.
      // `approveRequest`/`denyRequest` devuelven `{ok, level, completed, status}` — un
      // acuse de recibo, NO la solicitud. Hacer `setRequest(res.data)` dejaba la página en
      // blanco: el badge sobrevivía (porque `status` sí viene) y Empleado, Proyecto,
      // fechas y horas quedaban en `undefined`/0.00.
      // Se RE-CONSULTA el detalle, que además es lo único correcto: al aprobar cambian
      // también la cadena y `can_approve`, y esos los arma `getRequestById`. Devolver la
      // solicitud enriquecida desde el POST exigiría duplicar ahí los joins, la cadena y
      // los días hábiles — una segunda expresión de la misma lectura.
      await client.post(`/api/vacation/requests/${id}/${action}`, { comments: comments || null });
      const { data } = await client.get(`/api/vacation/requests/${id}`);
      setRequest(data);
      setComments('');
    } catch (err) {
      alert(err.response?.data?.error || `Error al ${action === 'approve' ? 'aprobar' : 'denegar'}.`);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCancel() {
    if (!confirm('¿Cancelar esta solicitud?')) return;
    setActionLoading(true);
    try {
      await client.delete(`/api/vacation/requests/${id}`);
      navigate('/vacaciones');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cancelar.');
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;
  // B-1: se QUEDA mostrando el motivo. Acá llegan las 7 plantillas de PTO.
  if (errorCarga) return <DetalleNoDisponible motivo={errorCarga} onVolver={() => navigate('/vacaciones')} volverLabel="Volver a Vacaciones" />;
  if (!request) return null;

  const cfg = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  // ⚠ V62 — `user_id` se retiró de `pto_requests` (V59). Las TRES identidades son hechos
  // distintos: `subject_user_id` (la cuenta del ausente, si tiene) · `submitted_by` (quien
  // la cargó) · `persona_id` (de quién es la ausencia).
  const isOwner = request.subject_user_id === user?.id || request.submitted_by === user?.id;
  const canCancel = isOwner && (request.status === 'pending' || request.status === 'approved');

  const chain = request.approval_chain || [];
  const pendingRecord = chain.find(r => r.status === 'pending');
  // ⚠⚠ Lo decide el BACKEND (`can_approve`), no esta pantalla. En any-of `approver_id`
  // está en NULL hasta que alguien actúa, así que buscar la fila propia en la cadena
  // —lo que hacía la versión vieja— da siempre false. Ver la nota en `getRequestById`.
  const canReview = !!request.can_approve;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 640, margin: '0 auto' }}>
      <button
        onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13, marginBottom: 12, fontFamily: 'Inter, sans-serif' }}
      >
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Header card */}
      <div style={{ ...bubbleStyle, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: cfg.bg, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={22} color={cfg.color} />
          </div>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>
              {TYPE_LABELS[request.type] || request.type}
            </h2>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 10px',
              borderRadius: 10, background: cfg.bg, color: cfg.color,
            }}>
              {cfg.label}
              {/* V62: el nivel vigente se DERIVA de la fila `pending` — `current_level`
                  se retiró en V59 para no tener el mismo hecho en dos lugares. */}
              {pendingRecord && request.status === 'pending' && (
                <span> · espera {LEVEL_ROLE[pendingRecord.level] || `nivel ${pendingRecord.level}`}</span>
              )}
            </span>
          </div>
        </div>

        {/* Details grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Empleado</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>{request.employee_name}</div>
            {request.submitted_by_name && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                Registrada por {request.submitted_by_name}
              </div>
            )}
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Horas</div>
            {/* V62: HORAS es la magnitud; los días hábiles son contexto derivado. */}
            <div style={{ color: NAVY, fontWeight: 500 }}>
              {(Number(request.hours_requested) || 0).toFixed(2)} h
              {request.business_days > 0 && (
                <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                  {' '}· {request.business_days} día{request.business_days !== 1 ? 's' : ''} hábil{request.business_days !== 1 ? 'es' : ''}
                </span>
              )}
            </div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Proyecto</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>
              {etiquetaProyecto(request.project_code, request.project_name, { sinNombre: '—' })}
            </div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Fecha inicio</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>{formatDate(request.start_date)}</div>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Fecha fin</div>
            <div style={{ color: NAVY, fontWeight: 500 }}>{formatDate(request.end_date)}</div>
          </div>
        </div>

        {request.reason && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: `1px solid ${BORDER_COLOR}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 2 }}>Motivo</div>
            <div style={{ fontSize: 13, color: NAVY }}>{request.reason}</div>
          </div>
        )}
      </div>

      {/* Cadena de aprobación — V62: dos niveles, inline.
          ⚰ Reemplaza a `ApprovalChainTimeline`, retirado: era un timeline vertical de
          L1-L5 con conectores, y para DOS filas ese componente era más andamio que dato.
          ⚠ Un nivel `skipped` se muestra CON SU RAZÓN: es información (dice por qué nadie
          tuvo que aprobar), no ausencia de información. */}
      {chain.length > 0 && (
        <div style={{ ...bubbleStyle, padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10 }}>
            Cadena de aprobación
          </div>
          {chain.map(c => {
            const st = CHAIN_STATE[c.status] || CHAIN_STATE.waiting;
            return (
              <div key={c.level} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '8px 0', borderTop: c.level > 1 ? `1px solid ${BORDER_COLOR}` : 'none',
              }}>
                <span style={{
                  fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                  background: st.bg, color: st.color, whiteSpace: 'nowrap', marginTop: 1,
                }}>
                  {st.label}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>
                    {LEVEL_ROLE[c.level] || `Nivel ${c.level}`}
                    {c.approver_display_name && (
                      <span style={{ fontWeight: 400, color: '#64748b' }}> · {c.approver_display_name}</span>
                    )}
                  </div>
                  {c.comments && (
                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>{c.comments}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Otras ausencias que se solapan — contexto para quien aprueba (V13).
          ⚠ V62: sale del CALENDARIO, que YA NO EMITE `type`. Acá se muestra solo quién y
          cuándo, a propósito: el tipo de la solicitud que se está revisando sí aparece
          arriba (es la que este aprobador autoriza), pero el de TERCEROS no — sería dato
          de salud de gente ajena a esta decisión. */}
      {!isOwner && overlapping.length > 0 && (
        <div style={{ ...bubbleStyle, padding: '14px 18px', marginBottom: 16, borderLeft: '3px solid #f59e0b' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
            Otras ausencias en este período ({overlapping.length})
          </div>
          {overlapping.map(v => (
            <div key={v.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
              fontSize: 12, color: '#78350f', flexWrap: 'wrap',
            }}>
              <span style={{ fontWeight: 600 }}>{v.user_name}</span>
              <span style={{ color: '#94a3b8' }}>·</span>
              <span>{formatDate(v.start_date)} — {formatDate(v.end_date)}</span>
              <span style={{ color: '#94a3b8' }}>·</span>
              <span>{v.full_day ? 'día completo' : `${v.hours_per_day} h/día`}</span>
            </div>
          ))}
        </div>
      )}

      {/* Approver actions */}
      {canReview && (
        <div style={{ ...bubbleStyle, padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 4 }}>
            Acción — {LEVEL_ROLE[pendingRecord?.level] || `Nivel ${pendingRecord?.level}`}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
            Esta solicitud requiere su aprobación.
          </div>
          <textarea
            value={comments}
            onChange={e => setComments(e.target.value)}
            placeholder="Comentarios (opcional)"
            rows={2}
            style={{ ...fieldStyle, resize: 'vertical', marginBottom: 12 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleAction('approve')}
              disabled={actionLoading}
              style={{ ...navyBtn, flex: 1, background: '#16a34a', fontSize: 13 }}
            >
              Aprobar
            </button>
            <button
              onClick={() => handleAction('deny')}
              disabled={actionLoading}
              style={{ ...navyBtn, flex: 1, background: '#dc2626', fontSize: 13 }}
            >
              Denegar
            </button>
          </div>
        </div>
      )}

      {/* Cancel button */}
      {canCancel && (
        <button
          onClick={handleCancel}
          disabled={actionLoading}
          style={{
            width: '100%', padding: '10px 0', fontSize: 13, fontWeight: 600,
            background: 'transparent', color: '#dc2626',
            border: '1.5px solid #fecaca', borderRadius: 8,
            cursor: 'pointer', fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Trash2 size={14} /> Cancelar Solicitud
        </button>
      )}
    </div>
  );
}
